import { isHttpsUrl, parseEmbedInput } from './embeds';
import { resolveMediaUrl } from './media';

// Condoned Media video posts: another creator's YouTube or Rumble video,
// published with our own commentary on the /videos page. A post is a video
// post when the backend stored a `video` subdocument on it:
//   { provider: 'youtube'|'rumble', id, url, start, title, channel,
//     channelUrl, thumbnail }
// (`id` = YouTube's 11-char id or Rumble's EMBED id; `url` = the original
// watch/page URL for the credit link.)
//
// Link parsing reuses utils/embeds.js (the in-article embed parser) and is
// mirrored server-side in backend/utils/video.js — keep them in sync.

// Public page listing every video post (site route; paginated as /videos/page/:n).
export const VIDEOS_PATH = '/videos';

export const VIDEO_URL_ERROR = 'Unrecognized video link — paste a YouTube (youtube.com/watch, youtu.be, /shorts/, /live/) or Rumble (rumble.com/v…-title.html or rumble.com/embed/…) link';

// The server saved the document but not its video link (a backend that
// predates Condoned Media), so it is listed with the articles.
export const VIDEO_NOT_STORED_MESSAGE = 'The server saved the document but not its video link, so it is listed under Articles, not on /videos. The backend probably needs updating; edit the document and choose Videos again once it is.';

const YOUTUBE_ID_PATTERN = /^[A-Za-z0-9_-]{11}$/;
const RUMBLE_EMBED_ID_PATTERN = /^[A-Za-z0-9]+(?:[.-][A-Za-z0-9]+)*$/;
// What parseEmbedInput hands back for the two providers.
const YOUTUBE_EMBED_SRC = /^https:\/\/www\.youtube-nocookie\.com\/embed\/([A-Za-z0-9_-]{11})(?:\?start=(\d+))?$/;
const RUMBLE_EMBED_SRC = /^https:\/\/rumble\.com\/embed\/([A-Za-z0-9.-]+)/;

const PROVIDER_LABELS = { youtube: 'YouTube', rumble: 'Rumble' };

export const getYouTubeThumbnailUrl = (id, quality = 'hqdefault') => (
    `https://i.ytimg.com/vi/${id}/${quality}.jpg`
);

export const getYouTubeWatchUrl = (id, start = 0) => (
    `https://www.youtube.com/watch?v=${id}${start > 0 ? `&t=${start}s` : ''}`
);

export const getRumbleEmbedPageUrl = (embedId) => `https://rumble.com/embed/${embedId}/`;

// Writers paste full links, links without https://, or bare YouTube ids.
const toLinkCandidate = (trimmed) => {
    if (YOUTUBE_ID_PATTERN.test(trimmed)) {
        return `https://youtu.be/${trimmed}`;
    }
    return /^[a-z][a-z0-9+\-.]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Classifies a video link the way the backend will:
 *   { provider: 'youtube', id, start, url }
 *   { provider: 'rumble', pageUrl }  — embed id must be looked up server-side
 *   { provider: 'rumble', embedId }
 *   null — not a supported video link
 */
export const parseVideoUrl = (input) => {
    if (typeof input !== 'string') {
        return null;
    }
    const trimmed = input.trim();
    if (!trimmed || trimmed.length > 2048) {
        return null;
    }

    const parsed = parseEmbedInput(toLinkCandidate(trimmed));
    if (!parsed) {
        return null;
    }
    if (parsed.type === 'resolve') {
        return { provider: 'rumble', pageUrl: parsed.url };
    }

    const youTube = YOUTUBE_EMBED_SRC.exec(parsed.src);
    // "videoseries" fits the id shape but is YouTube's playlist player.
    if (youTube && youTube[1] !== 'videoseries') {
        const start = youTube[2] ? Number.parseInt(youTube[2], 10) : 0;
        return { provider: 'youtube', id: youTube[1], start, url: getYouTubeWatchUrl(youTube[1], start) };
    }

    const rumble = RUMBLE_EMBED_SRC.exec(parsed.src);
    if (rumble && rumble[1].length <= 40 && RUMBLE_EMBED_ID_PATTERN.test(rumble[1])) {
        return { provider: 'rumble', embedId: rumble[1] };
    }
    return null;
};

// Stable identity of a parsed link: same key = same stored video.
export const getVideoLinkKey = (parsed) => {
    if (!parsed) {
        return '';
    }
    if (parsed.provider === 'youtube') {
        return `youtube:${parsed.id}:${parsed.start || 0}`;
    }
    return parsed.embedId ? `rumble-embed:${parsed.embedId}` : `rumble-page:${parsed.pageUrl}`;
};

/** Video post detection, shared by every surface (backend: `video.id` set). */
export const isVideoPost = (post) => Boolean(post && post.video && post.video.id && post.video.provider);

const isValidVideoId = (video) => (
    video.provider === 'youtube'
        ? YOUTUBE_ID_PATTERN.test(video.id)
        : (video.provider === 'rumble' && typeof video.id === 'string' && RUMBLE_EMBED_ID_PATTERN.test(video.id))
);

// The stored video when it is one we can play, else null.
export const getPostVideo = (post) => {
    if (!isVideoPost(post)) {
        return null;
    }
    return isValidVideoId(post.video) ? post.video : null;
};

export const getVideoProviderLabel = (videoOrProvider) => {
    const provider = typeof videoOrProvider === 'string' ? videoOrProvider : videoOrProvider?.provider;
    return PROVIDER_LABELS[provider] || 'Video';
};

/**
 * Player URL for the click-to-play facade (privacy-enhanced YouTube host:
 * no tracking cookies until the viewer presses play).
 */
export const getVideoEmbedSrc = (video, { autoplay = false } = {}) => {
    if (!video || !video.id || !isValidVideoId(video)) {
        return '';
    }
    if (video.provider === 'rumble') {
        return `${getRumbleEmbedPageUrl(video.id)}${autoplay ? '?autoplay=2' : ''}`;
    }
    const params = new URLSearchParams();
    if (autoplay) {
        params.set('autoplay', '1');
    }
    params.set('rel', '0');
    params.set('playsinline', '1');
    const start = Number(video.start);
    if (Number.isFinite(start) && start > 0) {
        params.set('start', String(Math.floor(start)));
    }
    return `https://www.youtube-nocookie.com/embed/${video.id}?${params.toString()}`;
};

// The original video page for the "Watch on YouTube/Rumble ↗" credit link.
export const getVideoSourceUrl = (video) => {
    if (!video || !isValidVideoId(video)) {
        return '';
    }
    if (isHttpsUrl(video.url)) {
        return video.url.trim();
    }
    return video.provider === 'youtube'
        ? getYouTubeWatchUrl(video.id, video.start)
        : getRumbleEmbedPageUrl(video.id);
};

export const getVideoChannelUrl = (video) => (isHttpsUrl(video?.channelUrl) ? video.channelUrl.trim() : '');

// The platform's own still: YouTube's hqdefault (always present), or the
// thumbnail Rumble's oEmbed reported when the post was saved.
export const getVideoThumbnailUrl = (video) => {
    if (!video || !isValidVideoId(video)) {
        return '';
    }
    if (video.provider === 'youtube') {
        return getYouTubeThumbnailUrl(video.id);
    }
    return isHttpsUrl(video.thumbnail) ? video.thumbnail.trim() : '';
};

// Thumbnails the backend assigned from the video: on a video post, the
// stored frame or any i.ytimg.com frame of that same video (a frame of
// another video is the writer's choice); without a video, nothing.
// Mirrors backend/utils/video.js. Anything else is the writer's own.
export const isAutoVideoThumbnail = (value, video = null) => {
    if (typeof value !== 'string' || !value.trim() || !video || !video.id) {
        return false;
    }
    const trimmed = value.trim();
    if (typeof video.thumbnail === 'string' && video.thumbnail.trim() && trimmed === video.thumbnail.trim()) {
        return true;
    }
    return video.provider === 'youtube' && trimmed.startsWith(`https://i.ytimg.com/vi/${video.id}/`);
};

// A thumbnail the writer uploaded themselves (never the auto video frame).
export const getPostCustomThumbnail = (post) => {
    if (!post || isAutoVideoThumbnail(post.thumbnail, isVideoPost(post) ? post.video : null)) {
        return '';
    }
    return resolveMediaUrl(post.thumbnail);
};

// Best available still for a post: the writer's upload, else the video
// frame, else whatever thumbnail is stored.
export const getPostThumbnailSrc = (post) => {
    const custom = getPostCustomThumbnail(post);
    if (custom) {
        return custom;
    }
    const videoThumb = getVideoThumbnailUrl(getPostVideo(post));
    if (videoThumb) {
        return videoThumb;
    }
    return resolveMediaUrl(post?.thumbnail);
};
